import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, BookOpen, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { publicService, UtilityPageSummary } from '../services/publicService';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { thumbnailSrc } from '../lib/imageUrl';
import { onThumbError } from '../components/ui/imageFallback';

export const UtilityCatalog: React.FC = () => {
  const [pages, setPages] = useState<UtilityPageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useDocumentMeta({
    title: 'Free Guides & Tutorials | ThinkShow',
    description: 'Learn something new with free, interactive guides on Python, React, Design, Data Science, and more. No signup required.',
    ogType: 'website',
  });

  useEffect(() => {
    publicService.getUtilityPages()
      .then(setPages)
      .catch(err => console.error('Failed to load utility pages:', err))
      .finally(() => setIsLoading(false));
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(pages.map(p => p.category))].sort();
    return ['All', ...cats];
  }, [pages]);

  const filteredPages = useMemo(() => {
    return pages.filter(page => {
      const matchesSearch =
        page.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        page.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === 'All' || page.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [pages, searchQuery, selectedCategory]);

  // Group by category for display
  const groupedByCategory = useMemo(() => {
    if (selectedCategory !== 'All') return null;
    const groups: Record<string, UtilityPageSummary[]> = {};
    for (const page of filteredPages) {
      if (!groups[page.category]) groups[page.category] = [];
      groups[page.category].push(page);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredPages, selectedCategory]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-primary-600" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="pb-20"
    >
      {/* Header */}
      <div className="bg-gradient-to-b from-primary-50 to-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-primary-600" />
              </div>
              <span className="text-sm font-bold text-primary-600 uppercase tracking-wider">
                Free Guides
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Learn something new today
            </h1>
            <p className="text-xl text-gray-500 max-w-2xl">
              Interactive guides on real topics. No signup, no paywall — just learn.
            </p>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8 max-w-xl"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search free guides..."
                className="w-full pl-12 pr-4 py-4 text-base bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-100 focus:border-primary-300 transition-all shadow-sm"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filters */}
        {categories.length > 2 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="flex flex-wrap gap-2 mb-10"
          >
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border ${
                  selectedCategory === cat
                    ? 'bg-gray-900 text-white border-gray-900 shadow-lg'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </motion.div>
        )}

        {/* Results */}
        {filteredPages.length > 0 ? (
          groupedByCategory ? (
            // Grouped view (when showing "All")
            <div className="space-y-12">
              {groupedByCategory.map(([category, categoryPages]) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                    {category}
                    <span className="text-xs text-gray-400 font-normal">
                      ({categoryPages.length} {categoryPages.length === 1 ? 'guide' : 'guides'})
                    </span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {categoryPages.map((page, index) => (
                      <UtilityCard key={page.id} page={page} index={index} />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            // Flat view (when category is selected)
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPages.map((page, index) => (
                <UtilityCard key={page.id} page={page} index={index} />
              ))}
            </div>
          )
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-32 bg-white rounded-3xl border border-dashed border-gray-200"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-50 mb-6">
              <BookOpen className="h-8 w-8 text-gray-300" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No guides found</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-8">
              {searchQuery
                ? `We couldn't find any guides matching "${searchQuery}"`
                : 'No guides available in this category yet.'}
            </p>
            <button
              onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
              className="px-6 py-3 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear Filters
            </button>
          </motion.div>
        )}

        {/* Bottom CTA to main courses */}
        {filteredPages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex flex-col items-center bg-gradient-to-br from-primary-50 to-white border border-primary-100 rounded-2xl px-10 py-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Ready to go deeper?
              </h3>
              <p className="text-sm text-gray-500 mb-5 max-w-sm">
                Our full courses cover everything from basics to mastery, with certificates and AI tutoring.
              </p>
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
              >
                Browse Full Courses
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

// Individual utility card
function UtilityCard({ page, index }: { page: UtilityPageSummary; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 * Math.min(index, 8) }}
    >
      <Link
        to={`/learn-free/${page.slug}`}
        className="group block h-full"
      >
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary-200 hover:shadow-card-hover transition-all duration-300 flex flex-col h-full transform hover:-translate-y-1">
          {/* Thumbnail */}
          {page.thumbnailUrl && (
            <div className="relative aspect-[2/1] bg-gray-100 overflow-hidden">
              <img
                src={thumbnailSrc(page.thumbnailUrl)}
                onError={onThumbError}
                alt={page.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3">
                <span className="bg-white/90 backdrop-blur px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-primary-700">
                  Free Guide
                </span>
              </div>
              <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 rounded-md text-[10px] font-bold text-gray-600">
                {page.category}
              </div>
            </div>
          )}

          <div className="p-6 flex flex-col flex-1">
            <h3 className="font-sans text-lg font-bold text-gray-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors tracking-tight">
              {page.title}
            </h3>
            <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed flex-1">
              {page.description}
            </p>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <BookOpen className="h-3.5 w-3.5" />
                  {page.slideCount} slides
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {page.estimatedMinutes} min
                </span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
